import {
  Actor,
  Color,
  Engine,
  Keys,
  Rectangle,
  vec as exVec,
  Font,
  Label,
  FontUnit,
} from 'excalibur'
import {
  advance,
  createM0Scene,
  DEFAULT_COLONIST_COUNT,
  isTileAreaFree,
  snapshot,
  tileCenter,
  type RenderSnapshot,
  type SimState,
} from '../sim'
import { enqueuePlacement, screenToTile, type Extent } from '../input/place'
import { enqueueAssignDelta } from '../input/assign'
import { CONTENT, STARTER_CHAIN } from '../content'

// Render adapter. Excalibur draws the sim each frame from a between-tick
// snapshot; it never mutates sim state and never holds a live reference into
// it. The sim advances in fixed steps, decoupled from the frame rate. Input
// enters only as commands on the queue. Nothing under sim/ imports this file.
//
// This is the M0 fun-gate scene: many square-colonists haul through one or more
// parallel mine → smelter → storage chains, queue at access slots, and pool
// when idle. Colonist and chain-group counts are swept from URL query params.

const MAX_FRAME_SECONDS = 0.1 // clamp catch-up so a stall can't spiral
const START_SEED = 1

// Chain-group vertical extent, mirrored from the sim layout, used only to size
// the canvas so every group is visible.
const GROUP_ROW_SPACING = 130
const SCENE_TOP = 120
const SCENE_WIDTH = 820

const EMPTY_COLOR = Color.fromHex('#f4a259') // hauling empty / idle
const CARRY_COLOR = Color.fromHex('#8ecae6') // carrying cargo
const BUILDING_COLOR = Color.fromHex('#2a3d45')
const BUILDING_STROKE = Color.fromHex('#5c7a89')
// A patch reads as a distinct earthy tint from the slate buildings, so the
// extractor sitting adjacent to its resource source is legible at a glance.
const PATCH_COLOR = Color.fromHex('#6b4f2a')
const PATCH_STROKE = Color.fromHex('#b98a3e')

// Shared colonist graphics — reused across all pooled actors so nothing is
// allocated per frame or per unit.
const EMPTY_GFX = new Rectangle({ width: 10, height: 10, color: EMPTY_COLOR })
const CARRY_GFX = new Rectangle({ width: 10, height: 10, color: CARRY_COLOR })

// Content id placed on click. A second storage building is an inert obstacle —
// its sink recipe produces nothing and, sitting past the chain groups, it is
// never assigned a haul — so it only adds a box for colonists to route around,
// which is exactly what the placement QA needs to watch.
const PLACEMENT_ID = 'building.storage'
const BUILDING_SIZE = 48 // matches the sim footprint, mirrored to size the ghost
// Placement ghost: one reusable actor tinted green when the hovered tile is
// placeable and red when it is occupied or out of bounds. Preallocated graphics
// so hovering never allocates.
const GHOST_OK_GFX = new Rectangle({
  width: BUILDING_SIZE,
  height: BUILDING_SIZE,
  color: Color.fromHex('#7fb80080'),
  strokeColor: Color.fromHex('#7fb800'),
  lineWidth: 2,
})
const GHOST_BAD_GFX = new Rectangle({
  width: BUILDING_SIZE,
  height: BUILDING_SIZE,
  color: Color.fromHex('#d1495b80'),
  strokeColor: Color.fromHex('#d1495b'),
  lineWidth: 2,
})

// Digit keys 1..9 select chain groups 0..8 for labor assignment. Index into this
// array IS the chain index the key selects.
const DIGIT_KEYS = [
  Keys.Digit1,
  Keys.Digit2,
  Keys.Digit3,
  Keys.Digit4,
  Keys.Digit5,
  Keys.Digit6,
  Keys.Digit7,
  Keys.Digit8,
  Keys.Digit9,
]

export interface SceneConfig {
  colonistCount: number
  groupCount: number
}

export interface GameHandles {
  engine: Engine
  state: SimState
  /** A fresh between-tick snapshot of the core. */
  view(): RenderSnapshot
  /** Smoothed frames-per-second, updated each frame. */
  fps(): number
  /** The colonist / chain-group counts this scene was built with. */
  config: SceneConfig
  stop(): void
}

// Upper bounds for the swept counts. The sweep is an internal aliveness tool, so
// these are generous — but a malformed URL (e.g. ?colonists=1e9) must not spawn a
// billion units or size a canvas to millions of pixels and freeze the tab.
const MAX_COLONISTS = 2000
const MAX_GROUPS = 40

/** Parse the swept colonist / chain-group counts from a URL query string. */
export function parseSceneConfig(search: string): SceneConfig {
  const params = new URLSearchParams(search)
  const asCount = (
    raw: string | null,
    fallback: number,
    max: number,
  ): number => {
    const n = Number(raw)
    if (!Number.isFinite(n) || n < 1) return fallback
    return Math.min(Math.floor(n), max)
  }
  return {
    colonistCount: asCount(
      params.get('colonists'),
      DEFAULT_COLONIST_COUNT,
      MAX_COLONISTS,
    ),
    groupCount: asCount(params.get('groups'), 1, MAX_GROUPS),
  }
}

/** Compact readout of what a building currently holds, e.g. `raw_ore:1`. */
function inventoryText(inventory: Readonly<Record<string, number>>): string {
  const held = Object.entries(inventory)
    .filter(([, n]) => n > 0)
    .map(([id, n]) => `${id.replace(/^item\./, '')}:${n}`)
  return held.length > 0 ? held.join(' ') : 'empty'
}

/**
 * Draw one building box plus its live inventory label. Buildings enter the sim
 * only via drained place_building commands, so they appear over time (the
 * starter chain on the first tick, player placements on click); the caller
 * reconciles one of these per building index as it first shows up in the
 * snapshot. The returned label is refreshed each frame — the tell for which
 * building holds what and whether haulers are heading to the right place.
 */
function addBuildingActor(
  engine: Engine,
  b: RenderSnapshot['buildings'][number],
): Label {
  const w = b.box.max.x - b.box.min.x
  const h = b.box.max.y - b.box.min.y
  const a = new Actor({
    x: (b.box.min.x + b.box.max.x) / 2,
    y: (b.box.min.y + b.box.max.y) / 2,
    anchor: exVec(0.5, 0.5),
  })
  a.graphics.use(
    new Rectangle({
      width: w,
      height: h,
      color: BUILDING_COLOR,
      strokeColor: BUILDING_STROKE,
      lineWidth: 2,
    }),
  )
  engine.add(a)

  const name = b.id.replace(/^building\./, '')
  const label = new Label({
    text: `${name} · ${inventoryText(b.inventory)}`,
    x: b.box.min.x,
    y: b.box.min.y - 6,
    font: new Font({ size: 10, unit: FontUnit.Px, color: Color.White }),
  })
  engine.add(label)
  return label
}

/**
 * Draw one resource patch as a tinted box with a label naming what it yields.
 * Patches are static map data seeded before the first tick, so a patch shows up
 * in the very first snapshot and never moves; the caller adds one actor per
 * patch index the first time it appears. Read-only from the snapshot.
 */
function addPatchActor(
  engine: Engine,
  p: RenderSnapshot['patches'][number],
): void {
  const w = p.box.max.x - p.box.min.x
  const h = p.box.max.y - p.box.min.y
  const a = new Actor({
    x: (p.box.min.x + p.box.max.x) / 2,
    y: (p.box.min.y + p.box.max.y) / 2,
    anchor: exVec(0.5, 0.5),
    z: -1, // behind buildings and colonists
  })
  a.graphics.use(
    new Rectangle({
      width: w,
      height: h,
      color: PATCH_COLOR,
      strokeColor: PATCH_STROKE,
      lineWidth: 2,
    }),
  )
  engine.add(a)

  const label = new Label({
    text: `patch · ${p.item.replace(/^item\./, '')}`,
    x: p.box.min.x,
    y: p.box.min.y - 6,
    font: new Font({ size: 10, unit: FontUnit.Px, color: Color.White }),
  })
  engine.add(label)
}

/**
 * One-line-per-chain labor readout: `served/target` headcount for each chain,
 * the selected chain marked, plus the pooled surplus. `served` counts the live
 * colonist affinity so the QA-er can watch labor move A -> B as they reassign.
 */
function laborReadout(state: SimState, selected: number): string {
  const served = new Array<number>(state.chains.length).fill(0)
  let pooled = 0
  for (const u of state.units) {
    if (u.chainId >= 0 && u.chainId < served.length) served[u.chainId] += 1
    else pooled += 1
  }
  const chains = state.chains
    .map((c, i) => {
      const mark = i === selected ? '>' : ' '
      return `${mark}chain ${i + 1}: ${served[i]}/${c.target}`
    })
    .join('   ')
  return `labor  ${chains}   pool: ${pooled}   [1-9 select, +/- assign]`
}

export function startGame(
  canvasElementId: string,
  config: SceneConfig,
): GameHandles {
  const height = Math.max(
    300,
    SCENE_TOP + config.groupCount * GROUP_ROW_SPACING + 40,
  )

  const engine = new Engine({
    width: SCENE_WIDTH,
    height,
    canvasElementId,
    backgroundColor: Color.fromHex('#12181b'),
    suppressPlayButton: true,
    suppressConsoleBootMessage: true,
  })

  // The sim owns its world coordinates (fixed, viewport-independent); the canvas
  // is sized to show them 1:1. Colonists enter only as spawn commands drained on
  // the first tick, so the scene is reproducible regardless of window size.
  const state = createM0Scene(CONTENT, [...STARTER_CHAIN], {
    masterSeed: START_SEED,
    colonistCount: config.colonistCount,
    groupCount: config.groupCount,
  })

  // The fixed sim-world region the canvas shows (rendered 1:1). The click->tile
  // transform scales screen pixels against this fixed region, never the window,
  // so a placement lands on the same tile regardless of canvas size.
  const world: Extent = { width: SCENE_WIDTH, height }
  // The click->tile transform scales screen pixels against `world`, so `viewport`
  // must report the SAME coordinate space Excalibur reports pointer positions in:
  // `screen.resolution` (logical units), NOT `drawWidth/drawHeight` (which include
  // the device-pixel-ratio backing scale). Using the backing size would misplace
  // clicks by the pixel ratio on any HiDPI/Retina display.
  const viewport = (): Extent => ({
    width: engine.screen.resolution.width,
    height: engine.screen.resolution.height,
  })

  // One reusable label per building index and one actor per colonist id, both
  // grown lazily as the snapshot first shows each — no per-frame allocation and
  // no reliance on inline construction (buildings arrive via the command drain).
  const buildingLabels = new Map<number, Label>()
  const colonistActors = new Map<number, Actor>()
  // Patches are static; track which indices already have an actor so each is
  // drawn once (no per-frame allocation).
  const patchIndices = new Set<number>()

  // Placement affordance: a single ghost box that snaps to the hovered tile,
  // green when placeable and red when occupied/out of bounds. It reads only the
  // snapshot grid; clicking enqueues a place_building command (input never
  // mutates state — the next tick's drain decides accept/reject).
  const ghost = new Actor({ anchor: exVec(0.5, 0.5), z: 100 })
  ghost.graphics.use(GHOST_OK_GFX)
  engine.add(ghost)

  // Latest between-tick snapshot. The pointer handlers read the placement grid
  // from here (never live sim state), refreshed each preupdate below.
  let latestView: RenderSnapshot = snapshot(state)

  engine.input.pointers.primary.on('move', (evt) => {
    const grid = latestView.grid
    const tile = screenToTile(
      evt.screenPos.x,
      evt.screenPos.y,
      viewport(),
      world,
      grid,
    )
    const c = tileCenter(grid, tile)
    ghost.pos = exVec(c.x, c.y)
    ghost.graphics.use(
      isTileAreaFree(grid, tile) ? GHOST_OK_GFX : GHOST_BAD_GFX,
    )
  })

  engine.input.pointers.primary.on('down', (evt) => {
    enqueuePlacement(
      state,
      latestView.grid,
      PLACEMENT_ID,
      evt.screenPos.x,
      evt.screenPos.y,
      viewport(),
      world,
    )
  })

  // Chain-level labor assignment for QA (no per-unit UI). Digit keys
  // 1..9 select a chain group; '=' / '+' assigns one more colonist to it and
  // '-' releases one, both enqueued as an absolute-target assign_labor command
  // (input never mutates sim state — the drain reconciles it). The selected
  // chain is closure state on the render side only.
  let selectedChain = 0
  engine.input.keyboard.on('press', (evt) => {
    const digit = DIGIT_KEYS.indexOf(evt.key)
    if (digit !== -1) {
      if (digit < state.chains.length) selectedChain = digit
      return
    }
    if (selectedChain >= state.chains.length) return
    // Base the delta on the latest queued-but-undrained assign_labor for this
    // chain if one is pending, else the drained sim target. Without this, two
    // presses before the next tick both read the same drained target and enqueue
    // the same absolute count, so key-mashing collapses to a single net change
    // whenever the display refreshes faster than the 60Hz sim tick.
    let current = state.chains[selectedChain].target
    for (let i = state.commandQueue.length - 1; i >= 0; i--) {
      const cmd = state.commandQueue[i]
      if (cmd.type === 'assign_labor' && cmd.chain === selectedChain) {
        current = cmd.count
        break
      }
    }
    if (evt.key === Keys.Equal || evt.key === Keys.NumpadAdd) {
      enqueueAssignDelta(state.commandQueue, selectedChain, current, 1)
    } else if (evt.key === Keys.Minus || evt.key === Keys.NumpadSubtract) {
      enqueueAssignDelta(state.commandQueue, selectedChain, current, -1)
    }
  })

  // Labor readout: one line per chain showing served/target headcount, the
  // selected chain marked. Render-only — it counts live chain affinity for the
  // QA-er to watch labor move A -> B; no sim state is added for display.
  const laborLabel = new Label({
    text: '',
    x: 8,
    y: 8,
    font: new Font({ size: 11, unit: FontUnit.Px, color: Color.White }),
  })
  engine.add(laborLabel)

  let smoothedFps = 60

  engine.on('preupdate', (evt) => {
    const frameSeconds = Math.min(evt.elapsed / 1000, MAX_FRAME_SECONDS)
    smoothedFps = smoothedFps * 0.9 + (1 / Math.max(frameSeconds, 1e-4)) * 0.1

    advance(state, CONTENT, frameSeconds)

    // Read a between-tick snapshot and sync actors from it. Buildings appear as
    // their place_building commands drain, so reconcile one box+label per
    // building index the first time it shows up, then refresh its held-inventory
    // readout — the tell for what sits where and whether haulers converge right.
    latestView = snapshot(state)
    // Patches are seeded before the first tick and never move; draw each once,
    // behind the buildings, so the extractor sits visibly on its source.
    for (let i = 0; i < latestView.patches.length; i++) {
      if (patchIndices.has(i)) continue
      addPatchActor(engine, latestView.patches[i])
      patchIndices.add(i)
    }
    for (let i = 0; i < latestView.buildings.length; i++) {
      const b = latestView.buildings[i]
      if (!buildingLabels.has(i)) {
        buildingLabels.set(i, addBuildingActor(engine, b))
      }
      const name = b.id.replace(/^building\./, '')
      buildingLabels.get(i)!.text = `${name} · ${inventoryText(b.inventory)}`
    }
    for (const u of latestView.units) {
      let actor = colonistActors.get(u.id)
      if (actor === undefined) {
        actor = new Actor({ anchor: exVec(0.5, 0.5) })
        engine.add(actor)
        colonistActors.set(u.id, actor)
      }
      actor.pos = exVec(u.pos.x, u.pos.y)
      // Colour tells hauling (carrying) apart from empty/idle at a glance.
      actor.graphics.use(u.cargo !== null ? CARRY_GFX : EMPTY_GFX)
    }

    laborLabel.text = laborReadout(state, selectedChain)
  })

  void engine.start()

  return {
    engine,
    state,
    view: () => snapshot(state),
    fps: () => smoothedFps,
    config,
    stop: () => engine.stop(),
  }
}
